import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import Input from "../../forms/Input";
import Button from "../../Button";
import _ from "lodash";
import Label from "../../forms/inputs/Label";
import CreateEditLineItems from "./line-items/CreateEditLineItems";
import { uploadToServer } from "../../../helpers/FIleHelper";

const PRODUCT_DEFAULTS = {
    id: "",
    job_id: "",
    product_type_id: "",
    name: "",
    reference: "",
    quantity: "",
    price: "",
    description: "",
    photo: null,
    product_line_items: [],
    order: "",
    has_order: 0,
};

const CreateEditProduct = (props) => {
    const {
        jobId = null,
        productId = null,
        onCancel = () => {},
        onComplete = () => {},
    } = props;

    const prepareDefaultValues = () => {
        const defaultProduct = PRODUCT_DEFAULTS;

        if (!!productId && productId !== "") {
            defaultProduct.id = parseInt(productId);
        }

        return defaultProduct;
    };

    const [submitting, setSubmitting] = useState(false);
    const [productTypes, setProductTypes] = useState([]);
    const [job, setJob] = useState(null);
    const [product, setProduct] = useState(prepareDefaultValues());
    const [lineItemString, setLineItemString] = useState("");
    const [errors, setErrors] = useState(null);
    const [productCount, setProductCount] = useState(null);

    useEffect(() => {
        load();
    }, [jobId, productId]);

    const load = () => {
        axios
            .get(
                route("web-api.job.product.config", {
                    job: jobId,
                    product_id: productId,
                }).toString()
            )
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.job) {
                        setJob(response.data.job);
                        setProductCount(response.data.job.product_count);
                    }

                    if (!!response.data.product_types) {
                        setProductTypes(response.data.product_types);
                    }

                    if (!!response.data.product) {
                        setProduct(response.data.product);
                    } else {
                        setProduct(PRODUCT_DEFAULTS);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    };

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setProduct(prepareDefaultValues());

        setSubmitting(false);
        setErrors(null);
    };
    const payload = () => {
        return {
            method: !!productId ? "patch" : "post",
            url: !!productId
                ? route("web-api.job.product.update", {
                      job: jobId,
                      product: productId,
                      order_index: productId ? productCount : productCount + 1,
                  }).toString()
                : route("web-api.job.product.store", { job: jobId }).toString(),
            data: {
                ...product,
                photo:
                    !!product && !!product.photo
                        ? uploadToServer(product.photo)
                        : null,
                order_index: productId ? productCount : productCount + 1,
            },
        };
    };

    const submit = () => {
        const data = payload();
        axios(data)
            .then((response) => {
                reset();
                onComplete(response.data.job_id);
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(
                        <div>
                            {!!error.response.data.message
                                ? error.response.data.message
                                : "An error occurred"}
                        </div>
                    );
                }

                setSubmitting(false);
            });
    };

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    };

    const onChange = (field, value) => {
        const clone = _.cloneDeep(product);

        clone[field] = value;

        setProduct(clone);
    };

    const processLineItemString = () => {
        const lineItemSplit = lineItemString.split("<br>");
        const processedItems = lineItemSplit.map((item, index) => {
            const splitItem = item.split(";");

            return {
                id: uuidv4(),
                name: !!splitItem && !!splitItem[0] ? splitItem[0] : "",
                detail: !!splitItem && !!splitItem[1] ? splitItem[1] : "",
                order: index + 1,
            };
        });

        onChange("product_line_items", processedItems);
        setLineItemString("");
    };

    return (
        <React.Fragment>
            <div className="row m-0 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"name"}
                        label={"Product Name"}
                        required={true}
                        type={"text"}
                        value={product.name}
                        disabled={submitting}
                        onChange={(value) => onChange("name", value)}
                        error={!!errors && !!errors.name ? errors.name : null}
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        name={"product_type_id"}
                        label={"Product Type"}
                        required={true}
                        type={"searchable-select"}
                        options={productTypes}
                        value={product.product_type_id}
                        disabled={submitting}
                        onChange={(value) => onChange("product_type_id", value)}
                        error={
                            !!errors && !!errors.product_type_id
                                ? errors.product_type_id
                                : null
                        }
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"reference"}
                        label={"Product Reference"}
                        required={true}
                        type={"text"}
                        value={product.reference}
                        disabled={submitting}
                        onChange={(value) => onChange("reference", value)}
                        error={
                            !!errors && !!errors.reference
                                ? errors.reference
                                : null
                        }
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        name={"quantity"}
                        label={"Product Quantity"}
                        required={true}
                        type={"number"}
                        value={product.quantity}
                        disabled={submitting || product.has_order === 1}
                        onChange={(value) => onChange("quantity", value)}
                        error={
                            !!errors && !!errors.quantity
                                ? errors.quantity
                                : null
                        }
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"price"}
                        label={"Product Price"}
                        required={true}
                        type={"number"}
                        value={product.price}
                        disabled={submitting || product.has_order === 1}
                        onChange={(value) => onChange("price", value)}
                        error={!!errors && !!errors.price ? errors.price : null}
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        name={"photo"}
                        label={"Upload Product Photo"}
                        required={false}
                        type={"file"}
                        value={product.photo}
                        disabled={submitting}
                        onChange={(value) => onChange("photo", value)}
                        error={!!errors && !!errors.photo ? errors.photo : null}
                        accepts={"image/*"}
                        uploadLabel={"Upload Image"}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 bespoke-input">
                    <div className={"form-group"}>
                        <Label
                            label={"Line Item String"}
                            htmlFor={"line_item_string"}
                            required={false}
                        />
                        <div className={"position-relative line-item-string"}>
                            <div
                                className={
                                    submitting
                                        ? "form-control shadow-none disabled"
                                        : "form-control shadow-none"
                                }
                            >
                                <input
                                    type="text"
                                    disabled={submitting}
                                    value={lineItemString}
                                    onChange={(event) =>
                                        setLineItemString(event.target.value)
                                    }
                                />
                                <div className={"controls"}>
                                    {!!lineItemString && (
                                        <React.Fragment>
                                            <button
                                                className={
                                                    "m-0 mt-2 mt-md-0 py-0 btn font-weight-bold px-3 btn-eh-lilac submit-button"
                                                }
                                                disabled={submitting}
                                                onClick={(event) =>
                                                    processLineItemString()
                                                }
                                            >
                                                <span>Submit</span>
                                            </button>
                                            <button
                                                className={
                                                    "m-0 mt-2 mt-md-0 ml-md-2 py-0 btn font-weight-bold px-3 btn-outline-danger clear-button"
                                                }
                                                disabled={submitting}
                                                onClick={(event) =>
                                                    setLineItemString("")
                                                }
                                            >
                                                <span>Clear</span>
                                            </button>
                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 m-0 p-0 bespoke-input">
                    <Input
                        name={"description"}
                        label={"Description"}
                        required={false}
                        type={"textarea"}
                        value={product.description}
                        disabled={submitting}
                        onChange={(value) => onChange("description", value)}
                        error={
                            !!errors && !!errors.description
                                ? errors.description
                                : null
                        }
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0 pb-2 border-bottom">
                <div className="col-12 m-0 p-0">
                    <CreateEditLineItems
                        lineItems={product.product_line_items}
                        onChange={(value) =>
                            onChange("product_line_items", value)
                        }
                        disabled={submitting}
                        errors={errors}
                        canAddItems={product.has_order !== 1}
                        canDeleteItems={product.has_order !== 1}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Button
                            onClick={() => setSubmitting(true)}
                            label={!!productId ? "Save Changes" : "Create"}
                            classes={"btn btn-eh-lilac m-0 mr-4"}
                        />
                        <Button
                            onClick={() => handleClose()}
                            label={"Cancel"}
                            classes={"btn btn-outline-eh-off-black m-0 mr-4"}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default CreateEditProduct;
